import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { slotBookingUrl, voucherPurchaseUrl, travelCompanionUrl } from '../utils/bookingLinks';
import { loginUrlWithReturn, SAFETY_FORUM_PATH } from '../utils/safeReturnUrl';
import './Home.css';

const LEGENDA_COUPON_CODE = 'LEGENDA10';

const Home = ({ variant }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, instructor } = useAuth();
  const isLegendaVariant = variant === 'legenda';
  const dateLocale = i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB';
  const [imageErrors, setImageErrors] = useState({});
  const [weather, setWeather] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [pricing, setPricing] = useState([]);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch('https://api.open-meteo.com/v1/forecast?latitude=47.4304&longitude=19.2278&current_weather=true');
        if (response.ok) {
          const data = await response.json();
          setWeather(data.current_weather ? { main: { temp: data.current_weather.temperature }, weather: [{ id: data.current_weather.weathercode }] } : null);
        }
      } catch (err) {
        console.log('Időjárás betöltési hiba:', err);
      }
    };
    fetchWeather();

    const loadPricing = async () => {
      try {
        const response = await axios.get('/api/pricing/active');
        setPricing(response.data.pricing || []);
      } catch (err) {
        console.error('Árak betöltési hiba:', err);
      }
    };
    loadPricing();

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  const handleImageError = (imageName) => {
    setImageErrors(prev => ({ ...prev, [imageName]: true }));
  };

  const hasHeaderImage = !imageErrors['header-bg'];
  
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getWeatherIcon = (code) => {
    if (code < 3) return '☀️';
    if (code < 50) return '🌤️';
    if (code < 70) return '🌧️';
    if (code < 80) return '🌨️';
    if (code < 90) return '⛈️';
    return '🌫️';
  };

  const pricingImages = [
    '/gallery/IMG_4327.JPEG',
    '/gallery/IMG_4406.JPEG',
    '/gallery/IMG_4700.JPEG',
    '/gallery/IMG_5095.JPEG'
  ];

  useEffect(() => {
    if (!isLegendaVariant || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', 'view_promotion', {
      promotion_id: 'legenda_postbooking',
      promotion_name: 'Legenda partner upsell',
      creative_slot: 'legenda_landing'
    });
  }, [isLegendaVariant]);

  const slotLink = (options = {}) =>
    slotBookingUrl({
      ...options,
      code: isLegendaVariant ? LEGENDA_COUPON_CODE : options.code
    });

  const salesOfferTiles = [
    {
      to: slotLink({ segment: 'sightseeing' }),
      img: '/offer-city-from-air.png',
      kickerKey: 'home.offerTileSightseeingKicker',
      titleKey: 'home.offerTileSightseeingTitle',
      bodyKey: 'home.offerTileSightseeingBody',
      ctaKey: 'home.bookSlot'
    },
    {
      to: voucherPurchaseUrl({ focused: true }),
      img: '/gallery/IMG_4406.JPEG',
      kickerKey: 'home.offerTileVoucherKicker',
      titleKey: 'home.offerTileVoucherTitle',
      bodyKey: 'home.offerTileVoucherBody',
      ctaKey: 'home.buyVoucher'
    },
    {
      to: '/gallery',
      img: '/gallery/IMG_4700.JPEG',
      kickerKey: 'home.offerTileGalleryKicker',
      titleKey: 'home.offerTileGalleryTitle',
      bodyKey: 'home.offerTileGalleryBody',
      ctaKey: 'home.viewGallery'
    }
  ];

  const pricingImageSrc = (price) =>
    price.image_path || pricingImages[price.id % pricingImages.length];

  return (
    <div className="home-page">
      {/* Hero szekció kép háttérrel */}
      <div className="header hero-section">
        <div
          className="hero-bg-fallback"
          style={{
            backgroundImage: hasHeaderImage
              ? `url('/gallery/IMG_4327.JPEG')`
              : 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          aria-hidden="true"
        />
        <img
          src="/gallery/IMG_4327.JPEG"
          alt=""
          style={{ display: 'none' }}
          onError={() => handleImageError('header-bg')}
        />
        
        {/* Repülőtéri infó sáv */}
        <div className="hero-airport-bar">
          <span className="airport-code">LHBS</span>
          <span className="airport-sep">•</span>
          <span className="airport-city">{t('home.airportCity')}</span>
          <span className="airport-live">
            <span className="airport-live-dot" />
            {t('home.airportLive')}
          </span>
        </div>

        <div className="hero-content">
          {/* Idő és időjárás */}
          <div className="hero-time-weather-box">
            <div className="time-widget">
              <div className="time-display">{formatTime(currentTime)}</div>
              <div className="date-display">{currentTime.toLocaleDateString(dateLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <div className="weather-widget">
              <span className="weather-live-dot" aria-hidden="true" />
              <span className="weather-icon">{weather?.weather?.[0]?.id != null ? getWeatherIcon(weather.weather[0].id) : '🌤️'}</span>
              <span className="weather-temp">{weather ? `${Math.round(weather.main.temp)}°C` : '—'}</span>
            </div>
          </div>
          <h1 className="hero-title-unified">
            {t(isLegendaVariant ? 'home.legendaHeroTitle' : 'home.heroTitle')}
          </h1>
          <p className="hero-subheadline">{t('home.heroSubtitle')}</p>
          <div className="hero-cta-buttons">
            <Link to={slotLink({ segment: 'sightseeing' })} className="btn-hero-cta btn-hero-cta-primary">
              {t('home.bookFlight')}
            </Link>
            <Link to={voucherPurchaseUrl({ focused: true })} className="btn-hero-cta btn-hero-cta-outline">
              {t('home.giftFlight')}
            </Link>
          </div>
          <p className="hero-detail-line">{t('home.heroDetailLine')}</p>
        </div>
      </div>

      <section className="sales-offers-section" id="flight-offers" aria-labelledby="sales-offers-heading">
        <div className="sales-offers-inner">
          <header className="sales-offers-head">
            <h2 id="sales-offers-heading" className="sales-offers-title">
              {t('home.offersSectionTitle')}
            </h2>
          </header>

          <div className="sales-offers-grid sales-offers-grid--tiles">
            {salesOfferTiles.map((tile) => (
              <Link key={tile.to} to={tile.to} className="sales-offer-card sales-offer-card--tile">
                <div className="sales-offer-card__media">
                  <img src={tile.img} alt="" loading="lazy" />
                </div>
                <div className="sales-offer-card__body">
                  <p className="sales-offer-card__kicker">{t(tile.kickerKey)}</p>
                  <h3 className="sales-offer-card__title">{t(tile.titleKey)}</h3>
                  <p className="sales-offer-card__lede">{t(tile.bodyKey)}</p>
                  <span className="sales-offer-card__cta">
                    <span>{t(tile.ctaKey)}</span>
                    <span className="sales-offer-card__cta-arrow" aria-hidden="true">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {pricing.length > 0 && (
            <>
              <header className="sales-offers-subhead">
                <h3 className="sales-offers-pricing-title">{t('home.offerPricingHeading')}</h3>
                <p className="sales-offers-pricing-sub">{t('home.offerPricingSub')}</p>
              </header>
              <div className="sales-offers-grid sales-offers-grid--pricing">
                {[...pricing].reverse().map((price) => (
                  <Link key={price.id} to={slotLink({ pricingId: price.id })} className="sales-offer-card sales-offer-card--price">
                    <div className="sales-offer-card__media">
                      <img src={pricingImageSrc(price)} alt="" loading="lazy" />
                    </div>
                    <div className="sales-offer-card__body">
                      <p className="sales-offer-card__kicker">{t('home.offerFrom')}</p>
                      <h3 className="sales-offer-card__title">{price.name}</h3>
                      <div className="sales-offer-card__price-row">
                        <span className="sales-offer-card__amount">{parseInt(price.amount, 10).toLocaleString('en-GB')}</span>
                        <span className="sales-offer-card__currency">€</span>
                      </div>
                      <p className="sales-offer-card__meta">
                        {t('home.aircraftPriceNotice', {
                          passengers: price.max_passengers || 3,
                          weight: Number(price.max_total_weight_kg || 260).toLocaleString('en-GB')
                        })}
                      </p>
                      {price.description && (
                        <p className="sales-offer-card__promo">
                          {price.description
                            .replace(/Alfa HUB/gi, 'PandAero Clubtag')
                            .replace(/február\s+végéig/gi, 'Február 28.-ig')}
                        </p>
                      )}
                      <span className="sales-offer-card__cta sales-offer-card__cta--block">
                        <span>{t('home.offerPriceCardCta')}</span>
                        <span className="sales-offer-card__cta-arrow" aria-hidden="true">→</span>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Miért OscAir? – 3 USP kártya */}
      <section className="features-section" aria-labelledby="why-oscair-title">
        <div className="container">
          <h2 id="why-oscair-title" className="section-title">{t('home.whyOscAir')}</h2>
          <p className="features-intro">{t('home.intro')}</p>
          <div className="features">
            <div className="feature-card feature-card-with-image">
              <div className="feature-image">
                {!imageErrors['budapest-1'] ? (
                  <img 
                    src="/gallery/IMG_4327.JPEG" 
                    alt={t('home.flightViewBudapest')}
                    onError={() => handleImageError('budapest-1')}
                  />
                ) : (
                  <div className="image-placeholder">{t('home.loadingImage')}</div>
                )}
                <div className="feature-overlay">
                  <span className="feature-icon">💰</span>
                </div>
              </div>
              <div className="feature-content">
                <h3>{t('home.costSharingTitle')}</h3>
                <p>{t('home.costSharingText')}</p>
              </div>
            </div>
            <div className="feature-card feature-card-with-image">
              <div className="feature-image">
                {!imageErrors['budapest-2'] ? (
                  <img 
                    src="/gallery/IMG_4406.JPEG" 
                    alt={t('home.flightViewSightseeing')}
                    onError={() => handleImageError('budapest-2')}
                  />
                ) : (
                  <div className="image-placeholder">{t('home.loadingImage')}</div>
                )}
                <div className="feature-overlay">
                  <span className="feature-icon">👨‍✈️</span>
                </div>
              </div>
              <div className="feature-content">
                <h3>{t('home.verifiedTitle')}</h3>
                <p>{t('home.verifiedText')}</p>
              </div>
            </div>
            <div className="feature-card feature-card-with-image">
              <div className="feature-image">
                {!imageErrors['budapest-3'] ? (
                  <img 
                    src="/gallery/IMG_4700.JPEG" 
                    alt={t('home.flightViewDanube')}
                    onError={() => handleImageError('budapest-3')}
                  />
                ) : (
                  <div className="image-placeholder">{t('home.loadingImage')}</div>
                )}
                <div className="feature-overlay">
                  <span className="feature-icon">⚡</span>
                </div>
              </div>
              <div className="feature-content">
                <h3>{t('home.easyBookingTitle')}</h3>
                <p>{t('home.easyBookingText')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA szekció animációval */}
      <div className="cta-section-modern">
        <div className="container">
          <div className="cta-content">
            <h2>{t('home.ctaTitle')}</h2>
            <p>{t('home.ctaSubtitle')}</p>
            <div className="cta-buttons-with-hints">
              <div className="cta-button-block">
                <Link to={slotLink({ segment: 'sightseeing' })} className="btn btn-primary btn-booking">
                  <span>{t('home.bookSlot')}</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <p className="cta-button-hint">{t('home.ctaHintSlot')}</p>
              </div>
              <div className="cta-button-block">
                <Link to={voucherPurchaseUrl({ focused: true })} className="btn btn-primary btn-booking">
                  <span>{t('home.buyVoucher')}</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <p className="cta-button-hint">{t('home.ctaHintVoucher')}</p>
              </div>
              <div className="cta-button-block">
                <Link to={travelCompanionUrl({ focused: true })} className="btn btn-primary btn-booking">
                  <span>{t('home.findCompanions')}</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <p className="cta-button-hint">{t('home.ctaHintCompanions')}</p>
              </div>
              <div className="cta-button-block">
                <Link
                  to={isAuthenticated ? SAFETY_FORUM_PATH : loginUrlWithReturn(SAFETY_FORUM_PATH)}
                  className="btn btn-primary btn-booking"
                  title={!isAuthenticated ? t('nav.safetyForumLoginHint') : undefined}
                >
                  <span>{t('nav.safetyForum')}</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <p className="cta-button-hint">
                  {isAuthenticated ? t('home.ctaHintSafetyForum') : t('home.ctaHintSafetyForumGuest')}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="contact-section">
        <div className="container">
          <h2>{t('home.contact')}</h2>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <h3>📍 {t('home.address')}</h3>
                <p>1112 Budapest, Repülőtéri út 2A, 2. emelet</p>
              </div>
              <div className="contact-item">
                <h3>✉️ {t('home.contactInfo')}</h3>
                <p><a href="mailto:info@pandaero.com">info@pandaero.com</a></p>
                <p><a href="https://www.pandaero.com" target="_blank" rel="noopener noreferrer">www.pandaero.com</a></p>
              </div>
            </div>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps?q=1112+Budapest,+Repülőtéri+út+2A&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="oscair.hu location"
              ></iframe>
              <div className="map-link">
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=1112+Budapest,+Repülőtéri+út+2A" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {t('home.openMap')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
